import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, HelpCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mdComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mt-2 mb-4" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="font-serif text-xl font-bold text-text-heading mt-8 mb-3 pb-2 border-b border-border/50" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-semibold text-lg text-text-heading mt-6 mb-2" {...props} />
  ),
  p: ({ node, ...props }) => <p className="text-sm leading-relaxed text-foreground/90 my-3" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-3 space-y-1.5 text-sm text-foreground/90" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-3 space-y-1.5 text-sm text-foreground/90" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node, ...props }) => <a className="text-primary underline hover:text-primary-hover" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-text-heading" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-primary/40 bg-primary/5 rounded-r-lg pl-4 pr-3 py-2 my-4 text-sm text-foreground/80" {...props} />
  ),
  hr: ({ node, ...props }) => <hr className="my-6 border-border/50" {...props} />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="bg-muted text-primary px-1.5 py-0.5 rounded text-[0.85em] font-mono" {...props} />
    ) : (
      <code className="font-mono text-xs" {...props} />
    ),
  pre: ({ node, ...props }) => (
    <pre className="bg-text-heading text-white/90 rounded-xl p-4 my-4 overflow-x-auto text-xs leading-relaxed" {...props} />
  ),
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border border-border/50 rounded-lg overflow-hidden" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-muted/60 text-left" {...props} />,
  th: ({ node, ...props }) => <th className="px-3 py-2 font-medium text-text-heading border-b border-border/50" {...props} />,
  td: ({ node, ...props }) => <td className="px-3 py-2 border-b border-border/30 align-top text-foreground/90" {...props} />,
};

export default function HelpPage() {
  const { user, language } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isAdmin = user && (user.membership_type === 'admin' || user.membership_type === 'vip');

  useEffect(() => {
    if (user && !isAdmin) navigate('/');
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/help`, { credentials: 'include' });
        if (!res.ok) throw new Error('load failed');
        const data = await res.json();
        if (!active) return;
        setDocs(data.docs || []);
        setActiveId((data.docs && data.docs[0]?.id) || null);
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const activeDoc = docs.find((d) => d.id === activeId);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">
          {language === 'sk' ? 'Prihláste sa prosím.' : 'Přihlaste se prosím.'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle py-8" data-testid="help-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading">
                {language === 'sk' ? 'Nápoveda a príručky' : 'Nápověda a příručky'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {language === 'sk'
                  ? 'Ako systém funguje a ako s ním pracovať'
                  : 'Jak systém funguje a jak s ním pracovat'}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin')}
            className="gap-2"
            data-testid="help-back-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'sk' ? 'Späť do adminu' : 'Zpět do adminu'}
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-muted-foreground bg-white rounded-2xl shadow-soft border border-border/40">
            <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
            {language === 'sk' ? 'Načítavam…' : 'Načítám…'}
          </div>
        ) : error ? (
          <div className="p-12 text-center text-muted-foreground bg-white rounded-2xl shadow-soft border border-border/40">
            {language === 'sk' ? 'Nepodarilo sa načítať nápovedu.' : 'Nepodařilo se načíst nápovědu.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-6">
            {/* Doc selector */}
            <div className="space-y-2 min-w-0">
              {docs.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setActiveId(d.id)}
                  data-testid={`help-doc-tab-${d.id}`}
                  className={cn(
                    'w-full text-left rounded-2xl border p-4 transition-all',
                    activeId === d.id
                      ? 'bg-primary/5 border-primary/40 shadow-soft'
                      : 'bg-white border-border/40 hover:border-primary/30'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5',
                        activeId === d.id ? 'bg-primary/15' : 'bg-muted'
                      )}
                    >
                      <BookOpen className={cn('w-4 h-4', activeId === d.id ? 'text-primary' : 'text-muted-foreground')} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-text-heading leading-tight">{d.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-snug">{d.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Doc content */}
            <div
              className="bg-white rounded-2xl shadow-soft border border-border/40 p-6 md:p-8 min-w-0 overflow-hidden"
              data-testid="help-content"
            >
              {activeDoc ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {activeDoc.content}
                </ReactMarkdown>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {language === 'sk' ? 'Vyberte príručku.' : 'Vyberte příručku.'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
